/**
 * The two content-heavy sections of Workflow Details — the authored steps and
 * the run history.
 *
 * Extracted from the page so they can be mounted in a DEV harness (and thus
 * asserted at a phone viewport by Playwright) without the page's auth gate and
 * live API. They own no data fetching: give them arrays, they render.
 *
 * Both were the source of the mobile-portrait defect: a long step instruction
 * widened the grid, which pushed the run status pill off-screen, and a failed
 * run's reason was concatenated into the step-marks line.
 */

import { runLiveness } from '../../agents/runLiveness.ts';
import type { WorkflowRunRecord } from '../../agents/workflowsApi.ts';
import type { DisplayStep } from '../../agents/workflowMarkdown.ts';
import { runWhenLabel, stepErrors, stepMarks, triggerLabel } from './workflowRunText.ts';

/** The authored steps of a workflow, or the raw markdown when unparsable. */
export function WorkflowSteps({
  steps,
  markdown,
}: {
  steps: readonly DisplayStep[];
  markdown: string;
}) {
  if (steps.length === 0) {
    return <pre className="om-wfd__raw">{markdown}</pre>;
  }
  return (
    <>
      {steps.map((s) => (
        <div className="om-wfd__step" key={s.index}>
          <span className="om-wfd__stepn">{s.index}</span>
          <span>
            {s.title.length > 0 ? <span className="om-wfd__stept">{s.title}</span> : null}
            <span className="om-wfd__stepi">{s.instruction}</span>
          </span>
        </div>
      ))}
    </>
  );
}

/** A run whose runtime died mid-step keeps `running` on disk forever, so the
 *  stored status would label a three-day-old corpse RUNNING. Report what the
 *  record actually evidences (see `runLiveness.ts`). */
function runStatusLabel(run: WorkflowRunRecord): string {
  return runLiveness(run) === 'abandoned' ? 'INTERRUPTED' : run.status.toUpperCase();
}

/** Interrupted runs borrow the aborted pill — same meaning: stopped, did not
 *  finish. Never the `running` pill, which pulses as though it were live. */
function runStatusClass(run: WorkflowRunRecord): string {
  return runLiveness(run) === 'abandoned' ? 'aborted' : run.status;
}

/** The 7-day run history. */
export function WorkflowRunHistory({ runs }: { runs: readonly WorkflowRunRecord[] }) {
  if (runs.length === 0) {
    return <p className="om-wfd__muted">No runs yet.</p>;
  }
  return (
    <>
      {runs.map((run) => (
        <div className="om-wfd__run" key={run.runId}>
          <div className="om-wfd__runhead">
            <span className="om-wfd__runwhen">{runWhenLabel(run.initiatedAt)}</span>
            <span className="om-wfd__trigger">{triggerLabel(run.trigger)}</span>
            <span className={`om-wfd__status om-wfd__status--${runStatusClass(run)}`}>
              {runStatusLabel(run)}
            </span>
            <span className="om-wfd__runid">{run.runId}</span>
          </div>
          <div className="om-wfd__runsteps" data-testid="run-steps">
            {stepMarks(run.steps)}
          </div>
          {/* Failure detail is its OWN block: mixed into the marks line above
              it was unreadable and forced sideways scrolling on a phone. */}
          {stepErrors(run.steps).map((e) => (
            <p className="om-wfd__runerror" role="alert" key={e.index}>
              <strong>Step {e.index} failed</strong>
              {e.error}
            </p>
          ))}
        </div>
      ))}
    </>
  );
}
